import { useMemo, useState } from "react";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ProductsModal from "src/components/modals/ProductsModal/ProductsModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useCreateInventoryRequest } from "../../inventory-management/list/api-inventory";
import { useGetInventories } from "../../vendor-view/inventory-management/api-inventories";
import { useGetInventory } from "./api-inventory";
import useInventoryRequestTableColumns from "./useInventoryRequestTableColumns";

const InventoryRequestList = () => {
  useSetActiveTab(TAB_NAMES.INVENTORY_REQUEST);
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateInventoryData,
    isValidating,
  } = useGetInventory({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(updateInventoryData);

  const inventoryData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useInventoryRequestTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  return (
    <>
      <ProductsModal
        isOpen={isProductsModalOpen}
        onClose={() => setIsProductsModalOpen(false)}
        useGetData={useGetInventories}
        usePostData={useCreateInventoryRequest}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Inventory Requests"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(inventoryData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Create Inventory",
            onClick: () => setIsProductsModalOpen(true),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={inventoryData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default InventoryRequestList;
